import { t } from "@travily/i18n";
import { Avatar } from "@ui/components/Avatar";
import { AvatarUpload } from "@ui/components/AvatarUpload";
import { ButtonDock } from "@ui/components/ButtonDock/ButtonDock";
import { ButtonGroup, ButtonGroupItem } from "@ui/components/ButtonGroup";
import { Dialog } from "@ui/components/Dialog";
import { FieldSet } from "@ui/components/FieldSet";
import { Icon } from "@ui/components/Icon";
import { StandardButton } from "@ui/components/StandardButton";
import { StandardDateField } from "@ui/components/StandardDateField";
import { StandardTextField } from "@ui/components/StandardTextField";
import { firstSelectedKey } from "@ui/lib/selection";
import { tv } from "@ui/lib/tv";
import type { DateValue, Selection } from "@ui/primitives/shared";
import { type ReactNode, useEffect, useRef } from "react";

const addPersonSheet = tv({
  slots: {
    fields: "flex flex-col gap-5",
    identityRow: "flex items-start gap-4",
    // Centres the circle on the input rather than on the field box: the box grows
    // when the name is rejected, and end- or centre-alignment then drags the photo
    // down with the error message. 28px clears the label (24px line + 4px gap),
    // less the 10px the 60px circle overhangs the 40px control on each side.
    avatar: "mt-[18px] size-15",
    nameField: "min-w-0 flex-1",
    // Grid rows 0fr -> 1fr, so switching to Guest expands the sheet instead of
    // snapping it: the dialog is centred, and a field appearing at full height
    // jerks everything above it upwards by half that height.
    guestField: [
      "grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out",
      "motion-reduce:transition-none",
    ],
    guestFieldInner: "min-h-0 overflow-hidden",
    // The row's own top gap, inside the collapsing region — a sibling gap would
    // survive the collapse and leave a dead 20px band under the Family/Guest pair.
    guestFieldSpacer: "pt-5",
  },
  variants: {
    isGuest: {
      true: { guestField: "grid-rows-[1fr]" },
    },
  },
});

export type AddPersonAudience = "family" | "guest";

export interface AddPersonSheetProps {
  presented: boolean;
  onPresentedChange: (presented: boolean) => void;
  /** Whether the person joins the household or is travelling with it this time. */
  audience: AddPersonAudience;
  onAudienceChange: (audience: AddPersonAudience) => void;
  name: string;
  onNameChange: (name: string) => void;
  nameError?: ReactNode;
  dateOfBirth: DateValue | null;
  onDateOfBirthChange: (dateOfBirth: DateValue | null) => void;
  /** Blur, not change: a date field fires onChange for every intermediate year. */
  onDateOfBirthBlur?: () => void;
  dateOfBirthError?: ReactNode;
  /** Latest selectable birthday — nobody is born tomorrow. */
  maxDateOfBirth: DateValue;
  /** Seeds the field's focus year so the spinner starts somewhere plausible. */
  placeholderDateOfBirth: DateValue;
  /** Guests only: who they are here with, e.g. "Alfie's friend". */
  relationship: string;
  onRelationshipChange: (relationship: string) => void;
  avatarPreview: string | null;
  onAvatarFileSelect: (file: File) => void;
  onAvatarRemove: () => void;
  onSubmit: () => void;
  isPending?: boolean;
  /**
   * Bump on every rejected submit to move focus to the first flagged field.
   * A token rather than the errors themselves: those also appear on blur, where
   * pulling focus back would trap the person in the field they just left.
   */
  errorFocusToken?: number;
}

/**
 * Adds a person to the household, from the SearchBar "Who" picker or the trip
 * "who visited" strip. Presentational only: the caller owns form state and
 * validation.
 *
 * Ordered by what the person filling it in already knows — the name first, then
 * the birthday, and only then how they relate to the household. The photo is
 * optional, so it rides alongside the name rather than taking a row of its own.
 */
export function AddPersonSheet({
  presented,
  onPresentedChange,
  audience,
  onAudienceChange,
  name,
  onNameChange,
  nameError,
  dateOfBirth,
  onDateOfBirthChange,
  onDateOfBirthBlur,
  dateOfBirthError,
  maxDateOfBirth,
  placeholderDateOfBirth,
  relationship,
  onRelationshipChange,
  avatarPreview,
  onAvatarFileSelect,
  onAvatarRemove,
  onSubmit,
  isPending,
  errorFocusToken,
}: AddPersonSheetProps) {
  const isGuest = audience === "guest";
  const styles = addPersonSheet({ isGuest });
  const initial = name.trim()[0]?.toUpperCase();
  const fieldsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!errorFocusToken) return;
    fieldsRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [errorFocusToken]);

  const handleAudienceChange = (keys: Selection) => {
    const selected = firstSelectedKey(keys);
    if (selected) onAudienceChange(selected as AddPersonAudience);
  };

  return (
    <Dialog.Root isOpen={presented} onOpenChange={onPresentedChange} size="sm">
      <Dialog.Content>
        <Dialog.Header onClose={() => onPresentedChange(false)} title={t("components.add_person_sheet.title")} />
        <Dialog.Body>
          <div className={styles.fields()} ref={fieldsRef}>
            <div className={styles.identityRow()}>
              <AvatarUpload
                className={styles.avatar()}
                fallback={
                  <Avatar.Root className="size-full text-xl" color="green">
                    {initial ?? <Icon className="size-6 text-green-800" name="user" />}
                  </Avatar.Root>
                }
                label={t("components.add_person_sheet.photo_label")}
                onFileSelect={onAvatarFileSelect}
                onRemove={onAvatarRemove}
                previewSrc={avatarPreview}
              />
              <StandardTextField
                className={styles.nameField()}
                errorMessage={nameError}
                isInvalid={Boolean(nameError)}
                label={t("components.add_person_sheet.name_label")}
                onChange={onNameChange}
                placeholder={t("components.add_person_sheet.name_placeholder")}
                value={name}
              />
            </div>

            <StandardDateField
              errorMessage={dateOfBirthError}
              isInvalid={Boolean(dateOfBirthError)}
              label={t("components.add_person_sheet.birthday_label")}
              leadIcon={<Icon name="date" shadow="none" type="sticker" />}
              maxValue={maxDateOfBirth}
              onBlur={onDateOfBirthBlur}
              onChange={onDateOfBirthChange}
              placeholderValue={placeholderDateOfBirth}
              value={dateOfBirth}
            />

            <FieldSet size="md">
              <FieldSet.Legend>{t("components.add_person_sheet.audience_label")}</FieldSet.Legend>
              <ButtonGroup
                disallowEmptySelection
                onSelectionChange={handleAudienceChange}
                selectedKeys={new Set([audience])}
                selectionMode="single"
                size="sm"
              >
                <ButtonGroupItem id="family">{t("components.add_person_sheet.audience_family")}</ButtonGroupItem>
                <ButtonGroupItem id="guest">{t("components.add_person_sheet.audience_guest")}</ButtonGroupItem>
              </ButtonGroup>
            </FieldSet>
          </div>

          <div className={styles.guestField()}>
            <div className={styles.guestFieldInner()} inert={!isGuest}>
              <div className={styles.guestFieldSpacer()}>
                <StandardTextField
                  description={t("components.add_person_sheet.relationship_description")}
                  isOptional
                  label={t("components.add_person_sheet.relationship_label")}
                  onChange={onRelationshipChange}
                  placeholder={t("components.add_person_sheet.relationship_placeholder")}
                  value={relationship}
                />
              </div>
            </div>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          {/* No cancel: the header's close control already backs out, and a second
              full-width pill would read as an equally weighted decision. */}
          <ButtonDock
            align="end"
            primaryAction={
              // Names out of the label: it grew with every keystroke and ran the
              // width of the sheet on a long one. The audience is the only thing
              // the button's wording needs to commit to.
              <StandardButton color="pink" isPending={isPending} onPress={onSubmit} size="44">
                {isGuest
                  ? t("components.add_person_sheet.submit_guest")
                  : t("components.add_person_sheet.submit_family")}
              </StandardButton>
            }
          />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
